/**
 * Оптимистичный PATCH аккаунта: сразу правит кэш ['telegram-accounts'],
 * при ошибке откатывает и показывает toast.error. После успеха обновляется
 * отчёт о расходах (цена покупки, KAN-735).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { EXPENSES_QUERY_KEY } from '@/services/api/queryKeys';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount, UpdateAccountBody } from '@/types/telegramAccount';

const QUERY_KEY = ['telegram-accounts'];

type PatchableFields = Pick<UpdateAccountBody, 'label' | 'persona_name' | 'purchase_cost_rub'>;

export function useAccountPatch(account: TelegramAccount, errorText: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchableFields) =>
      telegramAccountsApi.updateAccount(account.account_id, body, account.operator_id),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const prev = queryClient.getQueryData<TelegramAccount[]>(QUERY_KEY);
      queryClient.setQueryData<TelegramAccount[] | undefined>(QUERY_KEY, (list) =>
        list?.map((a) =>
          a.operator_id === account.operator_id && a.account_id === account.account_id
            ? { ...a, ...body }
            : a
        )
      );
      return { prev };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EXPENSES_QUERY_KEY });
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev);
      toast.error(errorText);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
